import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAdminArticles, deleteArticle } from '../../services/api';
import { Button } from '../../components/ui/button';
import { 
  Plus, FileText, Eye, Edit3, Clock, TrendingUp, Trash2, 
  PenTool, BookOpen, Crown, ExternalLink, MoreVertical,
  BarChart2, Calendar, Search, Filter
} from 'lucide-react';

// Role config
const ROLE_CONFIG = {
  admin: { label: 'Administrator', icon: Crown, color: 'from-amber-500 to-orange-600', canDelete: true, canEditAll: true },
  editor: { label: 'Editor', icon: PenTool, color: 'from-purple-500 to-indigo-600', canDelete: true, canEditAll: true },
  author: { label: 'Author', icon: BookOpen, color: 'from-blue-500 to-cyan-600', canDelete: false, canEditAll: false },
};

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-[#EAEAE8] p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">{label}</p>
          <p className="text-3xl font-bold text-[#1A1A1A] mt-2">{value.toLocaleString()}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-[#1A1A1A]" />
        </div>
      </div>
    </div>
  );
}

export default function EditorDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const roleConfig = ROLE_CONFIG[user?.role] || ROLE_CONFIG.author;

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['admin', 'editor', 'author'].includes(user.role)) {
      navigate('/');
      return;
    }
    loadArticles();
  }, [user, authLoading, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadArticles = async () => {
    try {
      const res = await getAdminArticles({});
      let allArticles = res.data.articles || [];
      
      // Authors can only see their own articles
      if (user?.role === 'author') {
        allArticles = allArticles.filter(a => a.author_id === user.user_id);
      }
      
      setArticles(allArticles);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleDelete = async (id) => {
    if (!roleConfig.canDelete) {
      alert('You do not have permission to delete articles');
      return;
    }
    if (!window.confirm('Delete this article?')) return;
    await deleteArticle(id);
    setArticles(prev => prev.filter(a => a.article_id !== id));
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  const stats = {
    total: articles.length,
    published: articles.filter(a => a.status === 'published').length,
    drafts: articles.filter(a => a.status === 'draft').length,
    totalViews: articles.reduce((sum, a) => sum + (a.views || 0), 0)
  };

  const filteredArticles = articles.filter(a => {
    const matchesSearch = a.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D93025]/30 border-t-[#D93025] rounded-full animate-spin" />
      </div>
    );
  }

  const Icon = roleConfig.icon;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="editor-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-sm border-b border-[#EAEAE8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[#737373] hover:text-[#1A1A1A] transition-colors">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </Link>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${roleConfig.color} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-[#1A1A1A]">{roleConfig.label} Dashboard</h1>
                <p className="text-xs text-[#737373]">Welcome back, {user?.name}</p>
              </div>
            </div>
          </div>
          <Link to="/editor/write">
            <Button className="bg-[#D93025] text-white hover:bg-[#B71C1C] transition-all" data-testid="new-article-btn">
              <Plus className="w-4 h-4 mr-2" />New Article
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Articles" value={stats.total} icon={FileText} color="from-[#D93025] to-red-600" />
          <StatCard label="Published" value={stats.published} icon={Eye} color="from-emerald-500 to-teal-600" />
          <StatCard label="Drafts" value={stats.drafts} icon={Clock} color="from-amber-500 to-orange-600" />
          <StatCard label="Total Views" value={stats.totalViews} icon={BarChart2} color="from-blue-500 to-indigo-600" />
        </div>

        {/* Role Permissions Info */}
        <div className="rounded-2xl bg-[#D93025]/10 border border-[#D93025]/20 p-5 mb-8">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${roleConfig.color} flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-[#1A1A1A]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1A1A1A]">Your Permissions as {roleConfig.label}</h3>
              <p className="text-sm text-[#4A4A4A] mt-1">
                {user?.role === 'admin' && 'Full access to all articles, users, and settings.'}
                {user?.role === 'editor' && 'Can create, edit, and delete all articles. Can manage categories.'}
                {user?.role === 'author' && 'Can create and edit your own articles only.'}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/30" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-64 h-10 pl-10 pr-4 rounded-xl bg-[#F5F5F0] border border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 text-sm focus:outline-none focus:border-[#D93025]/50"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-4 rounded-xl bg-[#F5F5F0] border border-[#EAEAE8] text-[#1A1A1A] text-sm focus:outline-none focus:border-[#D93025]/50"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <span className="text-sm text-[#737373]">{filteredArticles.length} articles</span>
        </div>

        {/* Articles List */}
        <div className="rounded-2xl bg-white border border-[#EAEAE8] overflow-hidden">
          {filteredArticles.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#F5F5F0] flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-[#1A1A1A]/20" />
              </div>
              <p className="text-[#737373] mb-4">No articles yet</p>
              <Link to="/editor/write">
                <Button className="bg-[#D93025] text-white">
                  Create your first article
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filteredArticles.map(art => {
                const canEdit = roleConfig.canEditAll || art.author_id === user?.user_id;
                const canDelete = roleConfig.canDelete;
                
                return (
                  <div key={art.article_id} className="flex items-center gap-4 p-5 hover:bg-[#F5F5F0] transition-colors" data-testid={`editor-article-${art.article_id}`}>
                    {/* Thumbnail */}
                    <div className="w-20 h-14 rounded-lg overflow-hidden bg-[#F5F5F0] flex-shrink-0">
                      {art.featured_image ? (
                        <img src={art.featured_image} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="w-6 h-6 text-[#1A1A1A]/20" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-medium text-[#1A1A1A] truncate">{art.title}</h3>
                        {art.featured && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#D93025]/20 text-[#D93025]">Featured</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#737373]">
                        <span className={`px-2 py-0.5 rounded-full ${
                          art.status === 'published' 
                            ? 'bg-emerald-500/10 text-emerald-400' 
                            : 'bg-amber-500/10 text-amber-400'
                        }`}>
                          {art.status}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(art.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {(art.views || 0).toLocaleString()} views
                        </span>
                        {art.category?.name && (
                          <span className="text-[#D93025]">{art.category.name}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-[#737373] hover:text-[#1A1A1A]"
                        onClick={() => window.open(`/article/${art.slug || art.article_id}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-[#737373] hover:text-[#1A1A1A]"
                          onClick={() => navigate(`/editor/write?edit=${art.article_id}`)}
                          data-testid={`edit-btn-${art.article_id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-400/60 hover:text-red-400"
                          onClick={() => handleDelete(art.article_id)}
                          data-testid={`delete-btn-${art.article_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Tips */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl bg-white border border-[#EAEAE8]">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-3">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <h4 className="font-semibold text-[#1A1A1A] mb-1">Boost Engagement</h4>
            <p className="text-sm text-[#737373]">Add compelling featured images to increase article views by up to 40%.</p>
          </div>
          <div className="p-5 rounded-2xl bg-white border border-[#EAEAE8]">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center mb-3">
              <PenTool className="w-5 h-5 text-blue-400" />
            </div>
            <h4 className="font-semibold text-[#1A1A1A] mb-1">AI Assistant</h4>
            <p className="text-sm text-[#737373]">Use AI to summarize and translate your content into Kannada automatically.</p>
          </div>
          <div className="p-5 rounded-2xl bg-white border border-[#EAEAE8]">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center mb-3">
              <Clock className="w-5 h-5 text-purple-400" />
            </div>
            <h4 className="font-semibold text-[#1A1A1A] mb-1">Save as Draft</h4>
            <p className="text-sm text-[#737373]">Work on articles at your own pace. Drafts are saved automatically.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
